import { createSelector } from 'reselect';
import { SettingsState } from '../reducers/api/settings';

interface RootState {
  apiSettings?: SettingsState;
}

export const OnlinePaymentMinValue = 0.51;

export const GeneralPaymentMinValue = 0.01;

export const getSettings = (state: RootState): SettingsState =>
  (state.apiSettings || {}) as SettingsState;

export const getCreditCardPercentFee = createSelector(
  [getSettings],
  (settings: SettingsState): number => settings.creditCardFee || 0
);

export const getVoucherMinimumQuantity = createSelector(
  [getSettings],
  (settings: SettingsState): number => settings.voucherMinimumQuantity || 0
);

export const getMaxOrderTotalAmount = createSelector(
  [getSettings],
  (settings: SettingsState): number => settings.voucherOrderMaxAmount || 0
);

export const getLunchAmounts = createSelector(
  [getSettings],
  (settings: SettingsState): number[] => [
    settings.lunchMaxAmount,
    ...settings.lunchMidAmounts,
    settings.lunchMinAmount
  ]
);

export const getRecreationalAmounts = createSelector(
  [getSettings],
  (settings: SettingsState): number[] => [
    settings.recreationalMaxAmount,
    settings.recreationalMinAmount
  ]
);

export const getMassageAmounts = createSelector(
  [getSettings],
  (settings: SettingsState): number[] => [
    settings.massageMaxAmount,
    settings.massageMinAmount
  ]
);

export const getTransportAmounts = createSelector(
  [getSettings],
  (settings: SettingsState): number[] => [
    settings.transportMaxAmount,
    settings.transportMinAmount
  ]
);

export const getWellbeingAmounts = createSelector(
  [getSettings],
  (settings: SettingsState): number[] => [
    settings.wellbeingMaxAmount,
    settings.wellbeingMinAmount
  ]
);

export const getReCaptchaSiteKey = createSelector(
  [getSettings],
  (settings: SettingsState): string => settings.reCaptchaSiteKey || ''
);

export const getOnlinePaymentMinAmount = createSelector(
  [getSettings],
  (settings: SettingsState): number =>
    settings.onlinePaymentMinAmount || OnlinePaymentMinValue
);

export const getOfflinePaymentMinAmount = createSelector(
  [getSettings],
  (settings: SettingsState): number =>
    settings.offlinePaymentMinAmount || GeneralPaymentMinValue
);

export const getFeesVatPercentage = createSelector(
  [getSettings],
  (settings: SettingsState): number => settings.feesVatPercentage || 0
);

export const getFeePercentage = createSelector(
  [getSettings],
  (settings: SettingsState): number => settings.feePercentage || 0
);

export const getIsLunchFundsLoadingTypePending = createSelector(
  [getSettings],
  (settings: SettingsState): boolean | undefined =>
    settings.isLunchFundsLoadingTypePending || undefined
);

export const getCurrentLunchFundsLoadingType = createSelector(
  [getSettings],
  (settings: SettingsState): string | undefined =>
    settings.currentLunchFundsLoadingType || undefined
);

export const getAnyDeferredCardOrders = createSelector(
  [getSettings],
  (settings: SettingsState): boolean => settings.anyDeferredCardOrders || false
);

export const getMaxWorkingDays = createSelector(
  [getSettings],
  (settings: SettingsState): number => settings.maxWorkingDays || 0
);
